// Libraries
import clsx from "clsx";
import {
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";

// Components
import Button from "@/Layouts/components/Buttons/Button";
import TabSelector from "@/Layouts/components/Tab/TabSelector";

// Context
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Constants
import { TABS } from "./constants";

// Tabs
import CustomerInformationTab from "./components/Tabs/CustomerInformationTab";
import ProspectInformationTab from "./components/Tabs/ProspectInformationTab";
import DesiredTradeVehicleTab from "./components/Tabs/DesiredTradeVehicleTab";

// Helpers
import { anyErrorInTab } from "./helpers";

function renderTabContent(tab: string) {
	switch (tab) {
		case "customer":
			return <CustomerInformationTab />;
		case "prospect":
			return <ProspectInformationTab />;
		case "vehicle":
			return <DesiredTradeVehicleTab />;
		default:
			return null;
	}
}

type TAddEditPopupDialogProps = {
	open?: boolean;
	canSave?: boolean;
	onCloseAction?: () => void;
	onAddAction?: (callback?: () => void) => void;
};

export default function AddEditPopupDialog(props: Readonly<TAddEditPopupDialogProps>) {
	const { open = false, canSave = true, onCloseAction = () => {}, onAddAction = () => {} } = props ?? {};
	const [tabIndex, setTabIndex] = useState<number>(0);

	const { leadErrors, isSavingLead } = useLeadsContext();

	const tabErrors = TABS.map((tab, index) => anyErrorInTab(leadErrors?.[tab.key] ?? {}) && index !== tabIndex);

	const handleTabOnChange = (index: number) => {
		setTabIndex(index);
	};

	const handleOnCloseAction = () => {
		if (isSavingLead) {
			return;
		}
		setTabIndex(0);
		onCloseAction?.();
	};

	function handleOnAddAction() {
		onAddAction?.(() => setTabIndex(0));
	}

	return (
		<Dialog
			disableEscapeKeyDown
			fullWidth
			PaperProps={{
				className: "!rounded-[10px] !w-[760px]",
			}}
			maxWidth={"md"}
			open={open}
			onClose={handleOnCloseAction}
		>
			<DialogTitle className="!pt-6 !pl-8 !pb-0">
				<Typography className={"!font-montserrat !text-2xl text-default !font-medium"}>Add Lead</Typography>
				<IconButton
					aria-label="close"
					sx={{ position: "absolute", right: 24, top: 20, color: (theme) => theme.palette.grey[500], zIndex: 1000 }}
					onClick={handleOnCloseAction}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent className="!p-0">
				<Grid container className="[&_.MuiGrid-container>.MuiGrid-item_.MuiTypography-root]:!text-[14.5px]">
					<Grid item xs={12} className="sticky top-[0px] z-[100] bg-white  rounded-lg">
						<TabSelector
							defaultTab={0}
							readOnly={false}
							data={{
								tabs: [...TABS.map((tab) => tab.label)],
								disabledTab: [],
								topSwShow: false,
								topSwitchAdditional: false,
								bottomSwShow: false,
							}}
							tabValue={handleTabOnChange}
							errors={tabErrors}
						/>
					</Grid>
					<Grid item xs={12} className="!mb-2">
						{TABS.map(({ key }, index) => (
							<Grid key={`add-lead-tab-${key}`} item xs={12} className={clsx("px-8 pt-6", { hidden: tabIndex !== index })}>
								{renderTabContent(key)}
							</Grid>
						))}
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions className="!p-6">
				<Box className="flex justify-end gap-[10px]">
					<Button
						classes={clsx({
							"!normal-case !font-sans !min-w-[82px] !min-h-[30px] !rounded-[6px]": true,
							"!bg-gray-blue": !isSavingLead,
							"!bg-disabled": isSavingLead,
						})}
						variant="contained"
						text="Cancel"
						onClick={handleOnCloseAction}
						disabled={isSavingLead}
					/>
					<Tooltip title={canSave ? "" : "There are invalid form input fields"} placement="top" arrow>
						<Box>
							<Button
								classes={clsx({
									"!normal-case !font-sans !min-w-[82px] !min-h-[30px] !rounded-[6px] !text-white": true,
									"!bg-primary": canSave && !isSavingLead,
									"!bg-disabled": !canSave || isSavingLead,
									"[&_.MuiButton-icon]:m-0": isSavingLead,
								})}
								variant="contained"
								text={!isSavingLead ? "Add" : ""}
								onClick={handleOnAddAction}
								disabled={!canSave}
								endIcon={isSavingLead && <CircularProgress color="inherit" size={16} className="m-1" />}
							/>
						</Box>
					</Tooltip>
				</Box>
			</DialogActions>
		</Dialog>
	);
}
